import type {
  ListTimesheetUnlocksQuery,
  TimesheetUnlock,
  UnlockSortField,
  UnlockStatusFilter,
} from '@clockwise/clients/timesheet-unlocks';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { TimesheetUnlockAudit } from '@/components/custom/timesheet-unlock-audit';
import { TimesheetUnlockFormSheet } from '@/components/custom/timesheet-unlock-form-sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { timesheetUnlocksApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { formatIsoDate } from '@/lib/utils';

const PAGE_SIZE = 25;
// Default to a column that's actually rendered ('fromDate' → the "Date(s)"
// header) so the default sort shows its direction affordance. 'createdAt' is
// a valid sort key but has no column, so it would leave every header neutral.
const DEFAULT_SORT_FIELD: UnlockSortField = 'fromDate';
const DEFAULT_SORT_DIR: 'asc' | 'desc' = 'desc';

const STATUS_LABELS: Record<UnlockStatusFilter, string> = {
  active: 'Active',
  inactive: 'Inactive',
  all: 'All',
};

function dateRange(u: TimesheetUnlock): string {
  return u.fromDate === u.toDate
    ? formatIsoDate(u.fromDate)
    : `${formatIsoDate(u.fromDate)} – ${formatIsoDate(u.toDate)}`;
}

/**
 * Summary list for the settings "Unlock" tab: server-paginated, sortable,
 * filterable table of unlocks with an early-revoke action. Columns —
 * Customer, Employee, Date(s), Auto-end, Reason — plus a status filter
 * (Active / Inactive / All). Customer/Employee/Reason aren't server-sortable
 * (the row stores ids + a reason blob; name sort would need a join), so only
 * the date columns carry sort affordances — mirroring how users.index treats
 * related-name columns. Loading / error / empty are distinct states (G15).
 */
export function TimesheetUnlockList() {
  const queryClient = useQueryClient();
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Add/edit sheet: `null` closed; `{ unlock: null }` = add; `{ unlock }` = edit.
  const [sheet, setSheet] = useState<{ unlock: TimesheetUnlock | null } | null>(null);

  // Local list state — this lives inside the settings tab, not a routed page,
  // so it isn't URL-bookmarkable (matches the other in-settings lists).
  const [status, setStatus] = useState<UnlockStatusFilter>('active');
  const [sortField, setSortField] = useState<UnlockSortField>(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(DEFAULT_SORT_DIR);
  const [page, setPage] = useState(0);

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const listQuery: ListTimesheetUnlocksQuery = {
    status,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.timesheetUnlocks.list(listQuery),
    queryFn: () => timesheetUnlocksApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => timesheetUnlocksApi.revoke(id),
    onMutate: (id) => setRevokingId(id),
    onSuccess: () => {
      // Revoking the last row on a non-first page would leave `offset` past
      // the new total and refetch an empty page. Step back one page so the
      // user lands on rows instead of a blank state — but ONLY under the
      // Active filter, the only one where revoke removes the row from the
      // result set. Under Inactive/All the revoked row stays (relabeled) and
      // the total is unchanged, so clamping there would wrongly yank the user
      // back a page.
      if (status === 'active' && page > 0 && (query.data?.data.length ?? 0) <= 1) {
        setPage((p) => Math.max(0, p - 1));
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheetUnlocks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      toast.success('Unlock ended. The period is locked again.');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to revoke unlock.'),
    onSettled: () => setRevokingId(null),
  });

  const columns = useMemo<ColumnDef<TimesheetUnlock>[]>(
    () => [
      {
        id: 'customer',
        header: 'Customer',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.customerId === null ? (
            <span className="text-muted-foreground">All customers</span>
          ) : (
            (row.original.customerName ?? 'Unknown customer')
          ),
      },
      {
        id: 'employee',
        header: 'Employee',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.userId === null ? (
            <span className="text-muted-foreground">All employees</span>
          ) : (
            (row.original.userName ?? 'Unknown employee')
          ),
      },
      {
        id: 'fromDate',
        header: 'Date(s)',
        cell: ({ row }) => <span className="tabular-nums">{dateRange(row.original)}</span>,
      },
      {
        id: 'autoEndDate',
        header: 'Auto-end',
        cell: ({ row }) => (
          <span className="tabular-nums">{formatIsoDate(row.original.autoEndDate)}</span>
        ),
      },
      {
        id: 'reason',
        header: 'Reason',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.reason ? (
            <span className="line-clamp-2 max-w-xs text-sm">{row.original.reason}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          // Active unlocks can be edited or ended early; expired/revoked rows
          // (visible under Inactive/All) are terminal, so show their state.
          row.original.active ? (
            <div className="flex items-center justify-end gap-1">
              <Button
                size="icon-sm"
                variant="ghost"
                title="Edit unlock"
                onClick={() => setSheet({ unlock: row.original })}
              >
                <Pencil />
                <span className="sr-only">Edit unlock</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={revoke.isPending && revokingId === row.original.id}
                onClick={() => revoke.mutate(row.original.id)}
              >
                {revoke.isPending && revokingId === row.original.id ? 'Ending…' : 'End now'}
              </Button>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">
              {row.original.revokedAt ? 'Revoked' : 'Expired'}
            </span>
          ),
      },
    ],
    [revoke, revokingId],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        setSortField(DEFAULT_SORT_FIELD);
        setSortDirection(DEFAULT_SORT_DIR);
      } else {
        setSortField(first.id as UnlockSortField);
        setSortDirection(first.desc ? 'desc' : 'asc');
      }
      setPage(0);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="unlock-status-filter" className="text-xs text-muted-foreground">
            Status
          </Label>
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v as UnlockStatusFilter);
              setPage(0);
            }}
          >
            <SelectTrigger id="unlock-status-filter" className="h-8 w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{STATUS_LABELS.active}</SelectItem>
              <SelectItem value="inactive">{STATUS_LABELS.inactive}</SelectItem>
              <SelectItem value="all">{STATUS_LABELS.all}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <TimesheetUnlockAudit />
          <Button size="sm" onClick={() => setSheet({ unlock: null })}>
            <Plus />
            Add unlock
          </Button>
        </div>
      </div>

      <div className="overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead
                      key={h.id}
                      // Convey sort state to assistive tech (icon alone is
                      // visual-only). 'none' on a sortable-but-unsorted column.
                      aria-sort={
                        sortable
                          ? dir === 'asc'
                            ? 'ascending'
                            : dir === 'desc'
                              ? 'descending'
                              : 'none'
                          : undefined
                      }
                    >
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex select-none items-center gap-1 hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody aria-busy={query.isLoading}>
            {query.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skel-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load unlocks.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {status === 'active'
                    ? 'No active or scheduled unlocks. Click “Add unlock” to re-open a locked period.'
                    : status === 'all'
                      ? 'No unlocks have been created yet.'
                      : 'No unlocks match this filter.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow key={r.id}>
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <TimesheetUnlockFormSheet
        open={sheet !== null}
        onOpenChange={(open) => {
          if (!open) setSheet(null);
        }}
        unlock={sheet?.unlock ?? null}
      />
    </div>
  );
}

const defaultCell: ColumnDef<TimesheetUnlock>['cell'] = ({ getValue }) => String(getValue() ?? '');
